/**
 * Settings Modal for Kodeon Stream
 *
 * Premium overlay modal with sidebar navigation, modeled after Loft's SettingsModal.
 * Opened via:
 *   - Mac menu bar → Kodeon Stream → Settings...
 *   - Keyboard shortcut ⌘,
 *   - Developer Tools menu item
 *
 * Sections:
 *   General      — Theme, accessibility
 *   Startup      — Default landing page
 *   Shortcuts    — Keyboard shortcut reference
 *   Environment  — Backend environment switcher (prod/dev)
 *   About        — App version, environment
 *
 * Note: Data Display, Notifications, and Developer sections were removed
 * because none of their controls were wired up to actual functionality.
 * Their store fields remain in `settings-store.ts` for now to avoid
 * breaking persisted localStorage shapes — they are simply not surfaced
 * in the UI until consumers exist.
 */

import { useCallback, useEffect } from 'react'
import {
  useSettingsStore,
  type SettingsSection,
  LANDING_PAGE_OPTIONS,
} from '@/shared/stores/settings-store'
import { useEnvironmentStore } from '@/shared/stores/environment-store'
import { type ThemePreference } from '@/shared/stores/theme-store'
import { settings, SETTINGS_KEYS } from '@/features/settings/lib/settings'
import { useSetting } from '@/features/settings/hooks/use-setting'
import {
  PAGE_LOCKS,
  devOverrideFor,
  hasAnyDevOverride,
} from '@/lib/page-locks'
import { UpdatesSection } from '@/features/settings/components/UpdatesSection'
import '../../styles/settings-modal.css'

// ── Nav items ─────────────────────────────────────────────────────────────────

interface NavItem {
  id: SettingsSection
  label: string
  icon: React.ReactNode
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'general',
    label: 'General',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M2.5 14c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'startup',
    label: 'Startup',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5v6.5M5 4.5l3-3 3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 11.5v1.5a1.5 1.5 0 001.5 1.5h9a1.5 1.5 0 001.5-1.5v-1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'shortcuts',
    label: 'Shortcuts',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="4" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M4 7h2M10 7h2M5 10h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'environment',
    label: 'Environment',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="2" y="4" width="12" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" />
        <path d="M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.3" />
        <circle cx="8" cy="9" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'updates',
    label: 'Updates',
    // Download-into-tray glyph — matches the lucide Download icon used
    // inside UpdatesSection but stroked to fit the 14×14 nav SVG style.
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5v8M5 6.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 11.5v1.5a1.5 1.5 0 001.5 1.5h9a1.5 1.5 0 001.5-1.5v-1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'about',
    label: 'About',
    icon: (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M8 7v4M8 5h.01" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
]

// ── Shared primitives ─────────────────────────────────────────────────────────

function Toggle({ checked, onChange, id, label }: {
  checked: boolean
  onChange: (v: boolean) => void
  id?: string
  label?: string
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      id={id}
      className={`sm-toggle${checked ? ' on' : ''}`}
      onClick={() => onChange(!checked)}
      type="button"
    />
  )
}

function SettingRow({ label, description, children }: {
  label: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="sm-row">
      <div className="sm-row-info">
        <span className="sm-row-label">{label}</span>
        {description && <span className="sm-row-description">{description}</span>}
      </div>
      <div className="sm-row-control">{children}</div>
    </div>
  )
}

function PillSelector<T extends string>({ options, value, onChange }: {
  options: { id: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="sm-pill-row" role="radiogroup">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          role="radio"
          aria-checked={value === opt.id}
          className={`sm-pill-btn${value === opt.id ? ' active' : ''}`}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// ── Section: General ──────────────────────────────────────────────────────────

function GeneralSection() {
  const themePreference = useSetting<ThemePreference>(
    SETTINGS_KEYS.theme,
    () => settings.theme.get(),
    'system',
  )

  const reduceMotion = useSettingsStore((s) => s.reduceMotion)
  const setReduceMotion = useSettingsStore((s) => s.setReduceMotion)

  return (
    <div className="settings-page">
      {/* Theme */}
      <section className="sm-section">
        <p className="sm-section-title">Appearance</p>
        <div className="sm-theme-row">
          {(['system', 'dark', 'light'] as ThemePreference[]).map((t) => (
            <button
              key={t}
              type="button"
              className={`sm-theme-btn${themePreference === t ? ' active' : ''}`}
              onClick={() => settings.theme.set(t)}
            >
              <div className="sm-theme-swatch">
                {t === 'system' ? (
                  <><div className="sm-swatch-dark" /><div className="sm-swatch-light" /></>
                ) : t === 'dark' ? (
                  <div className="sm-swatch-dark" style={{ flex: 1 }} />
                ) : (
                  <div className="sm-swatch-light" style={{ flex: 1 }} />
                )}
              </div>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </section>

      {/* Accessibility */}
      <section className="sm-section">
        <p className="sm-section-title">Accessibility</p>
        <div className="sm-card">
          <SettingRow label="Reduce motion" description="Minimise animations for accessibility.">
            <Toggle checked={reduceMotion} onChange={setReduceMotion} />
          </SettingRow>
        </div>
      </section>

      <PreviewSubsection />
    </div>
  )
}

// ── Subsection: Preview (in-development pages) ────────────────────────────────

/**
 * "Preview" subsection of the General settings page.
 *
 * Page locks are managed per-page and per-mount inside the `<PageLock>`
 * component itself, so there is no longer a global "show in-progress pages"
 * toggle for the user to flip — every locked page must be bypassed
 * individually via its own "Show me anyway" button and the bypass only
 * lasts for the current view.
 *
 * What remains here is purely informational: when a developer has a
 * `.dev/page-locks.json` file forcing specific pages into a particular
 * state, we surface that so they understand why the UI is behaving the way
 * it is. The block renders nothing in production builds (where the dev
 * override path is tree-shaken out by `import.meta.env.DEV`) and in dev
 * builds when no override file is present.
 *
 * See `src/lib/page-locks/`.
 */
function PreviewSubsection() {
  const devOverrideActive = hasAnyDevOverride()
  if (!devOverrideActive) return null

  const devOverrideEntries = Object.keys(PAGE_LOCKS)
    .map((pageId) => {
      const value = devOverrideFor(pageId)
      if (!value) return null
      return { pageId, value, title: PAGE_LOCKS[pageId]?.title ?? pageId }
    })
    .filter((e): e is { pageId: string; value: 'locked' | 'unlocked'; title: string } => e !== null)

  if (devOverrideEntries.length === 0) return null

  return (
    <section className="sm-section">
      <p className="sm-section-title">Preview</p>
      <div
        className="sm-card"
        style={{
          border: '1px solid rgba(245, 158, 11, 0.25)',
          background: 'rgba(245, 158, 11, 0.06)',
        }}
      >
        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'rgb(245, 158, 11)' }}>
            Dev override active
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
            <code style={{ fontFamily: 'var(--font-mono)' }}>.dev/page-locks.json</code> is
            forcing the lock state for the pages below. This file is local to your working
            tree and is ignored in production builds.
          </span>
          <ul style={{ margin: '4px 0 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {devOverrideEntries.map(({ pageId, value, title }) => (
              <li key={pageId} style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                  {pageId}
                </span>
                {' → '}
                <span style={{ fontWeight: 500 }}>{value}</span>
                <span style={{ color: 'var(--text-tertiary)' }}>{` (${title})`}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

// ── Section: Startup ──────────────────────────────────────────────────────────

function StartupSection() {
  const landingPage = useSettingsStore((s) => s.landingPage)
  const setLandingPage = useSettingsStore((s) => s.setLandingPage)

  // Group landing-page options by their `group` field (Observe, Operate, …)
  // so the dropdown is scannable rather than a flat list of 18 routes.
  const groupedOptions = LANDING_PAGE_OPTIONS.reduce<Record<string, typeof LANDING_PAGE_OPTIONS>>(
    (acc, opt) => {
      acc[opt.group] = acc[opt.group] || []
      acc[opt.group].push(opt)
      return acc
    },
    {},
  )

  const isKodeonSplash = landingPage.kind === 'kodeon-splash'
  const selectedRoute = landingPage.kind === 'route' ? landingPage.route : ''

  return (
    <div className="settings-page">
      <section className="sm-section">
        <p className="sm-section-title">Default landing page</p>
        <p className="sm-section-description">
          Choose what Stream shows when you sign in. The transition particle-traces
          your destination before fading into the live UI.
        </p>

        <div className="sm-card">
          {/* Kodeon splash — default */}
          <button
            type="button"
            role="radio"
            aria-checked={isKodeonSplash}
            className="sm-row"
            style={{
              width: '100%',
              cursor: 'pointer',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              padding: '12px 4px',
            }}
            onClick={() => setLandingPage({ kind: 'kodeon-splash' })}
            data-testid="settings-startup-kodeon-splash"
          >
            <div className="sm-row-info">
              <span className="sm-row-label">Kodeon splash</span>
              <span className="sm-row-description">
                Empty shell with the brand "Kodeon" spelled out in particles. Pick any
                sidebar item to start working.
              </span>
            </div>
            <div className="sm-row-control">
              <RadioDot checked={isKodeonSplash} />
            </div>
          </button>

          {/* Specific page */}
          <button
            type="button"
            role="radio"
            aria-checked={!isKodeonSplash}
            className="sm-row"
            style={{
              width: '100%',
              cursor: 'pointer',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              padding: '12px 4px',
            }}
            onClick={() => {
              if (isKodeonSplash) {
                // Default the route picker to /events when switching off Kodeon.
                setLandingPage({ kind: 'route', route: '/events' })
              }
            }}
            data-testid="settings-startup-specific-page"
          >
            <div className="sm-row-info">
              <span className="sm-row-label">Specific page</span>
              <span className="sm-row-description">
                Land directly on a chosen page after sign-in.
              </span>
              {!isKodeonSplash && (
                <select
                  className="sm-select"
                  style={{ marginTop: 8, maxWidth: 260 }}
                  value={selectedRoute}
                  onChange={(e) =>
                    setLandingPage({ kind: 'route', route: e.target.value })
                  }
                  onClick={(e) => e.stopPropagation()}
                  data-testid="settings-startup-page-select"
                >
                  {Object.entries(groupedOptions).map(([group, opts]) => (
                    <optgroup key={group} label={group}>
                      {opts.map((opt) => (
                        <option key={opt.route} value={opt.route}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>
            <div className="sm-row-control">
              <RadioDot checked={!isKodeonSplash} />
            </div>
          </button>
        </div>
      </section>
    </div>
  )
}

function RadioDot({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `1.5px solid ${checked ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
        background: checked ? 'var(--accent-bg-light)' : 'transparent',
        transition: 'all 120ms ease',
      }}
    >
      {checked && (
        <span
          style={{
            display: 'block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--accent-primary)',
          }}
        />
      )}
    </span>
  )
}

// ── Section: Shortcuts ────────────────────────────────────────────────────────

const SHORTCUTS = [
  { label: 'Open settings', keys: ['⌘', ','] },
  { label: 'Command palette', keys: ['⌘', 'K'] },
  { label: 'Toggle session panel', keys: ['⌘', '\\'] },
  { label: 'Toggle shortcuts overlay', keys: ['⌘', '/'] },
  { label: 'Navigate to Events', keys: ['⌘', '1'] },
  { label: 'Navigate to Queue', keys: ['⌘', '2'] },
  { label: 'Navigate to Memory', keys: ['⌘', '3'] },
  { label: 'Navigate to Agents', keys: ['⌘', '4'] },
  { label: 'Zoom in', keys: ['⌘', '+'] },
  { label: 'Zoom out', keys: ['⌘', '-'] },
  { label: 'Reset zoom', keys: ['⌘', '0'] },
  { label: 'Fullscreen', keys: ['⌃', '⌘', 'F'] },
]

function ShortcutsSection() {
  return (
    <div className="settings-page">
      <section className="sm-section">
        <p className="sm-section-title">Keyboard Shortcuts</p>
        <p className="sm-section-description">
          Navigate Stream faster with keyboard shortcuts. These are also available via ⌘/ anywhere in the app.
        </p>
        <div className="sm-shortcut-list">
          {SHORTCUTS.map((s) => (
            <div key={s.label} className="sm-shortcut-row">
              <span className="sm-shortcut-label">{s.label}</span>
              <span className="sm-shortcut-keys">
                {s.keys.map((k, i) => <span key={i} className="sm-kbd">{k}</span>)}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

// ── Section: Environment ──────────────────────────────────────────────────────

function EnvironmentSection() {
  const active = useEnvironmentStore((s) => s.active)
  const configs = useEnvironmentStore((s) => s.configs)
  const switchEnvironment = useEnvironmentStore((s) => s.switchEnvironment)

  if (!configs) {
    return (
      <div className="settings-page">
        <section className="sm-section">
          <p className="sm-section-title">Environment</p>
          <p className="sm-section-description">Loading environment configurations...</p>
        </section>
      </div>
    )
  }

  const prodConfig = configs.prod
  const devConfig = configs.dev

  return (
    <div className="settings-page">
      <section className="sm-section">
        <p className="sm-section-title">Environment</p>
        <p className="sm-section-description">
          Choose which backend environment to connect to. Switching environments will reset all active connections and reload the application.
        </p>
        <div className="sm-card">
          <SettingRow
            label="Active Environment"
            description={`Currently connected to ${active === 'prod' ? 'Production' : 'Development'}`}
          >
            <PillSelector
              options={[
                { id: 'prod' as const, label: 'Production' },
                { id: 'dev' as const, label: 'Development' },
              ]}
              value={active}
              onChange={(env) => switchEnvironment(env)}
            />
          </SettingRow>
        </div>
      </section>

      <section className="sm-section">
        <p className="sm-section-title">Environment Details</p>
        <div className="sm-card">
          <div className="sm-row">
            <div className="sm-row-info">
              <span className="sm-row-label">Production</span>
              <span className="sm-row-description" style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                {prodConfig.gateway_url}
              </span>
              {prodConfig.error && (
                <span className="sm-row-description" style={{ color: 'var(--color-error)' }}>
                  {prodConfig.error}
                </span>
              )}
            </div>
            <div className="sm-row-control">
              {prodConfig.unavailable ? (
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Unavailable</span>
              ) : active === 'prod' ? (
                <span style={{ fontSize: '11px', color: 'rgb(34, 197, 94)', fontWeight: 600 }}>Active</span>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Available</span>
              )}
            </div>
          </div>
          <div className="sm-row">
            <div className="sm-row-info">
              <span className="sm-row-label">Development</span>
              <span className="sm-row-description" style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                {devConfig.gateway_url}
              </span>
              {devConfig.error && (
                <span className="sm-row-description" style={{ color: 'var(--color-error)' }}>
                  {devConfig.error}
                </span>
              )}
            </div>
            <div className="sm-row-control">
              {devConfig.unavailable ? (
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Unavailable</span>
              ) : active === 'dev' ? (
                <span style={{ fontSize: '11px', color: 'rgb(245, 158, 11)', fontWeight: 600 }}>Active</span>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Available</span>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

// ── Section: About ────────────────────────────────────────────────────────────

function AboutSection() {
  return (
    <div className="settings-page">
      <section className="sm-section">
        <p className="sm-section-title">About Kodeon Stream</p>
        <div className="sm-about-card">
          <div className="sm-about-row">
            <span className="sm-about-label">Application</span>
            <span className="sm-about-value">Kodeon Stream</span>
          </div>
          <div className="sm-about-row">
            <span className="sm-about-label">Platform</span>
            <span className="sm-about-value">Tauri + React</span>
          </div>
          <div className="sm-about-row">
            <span className="sm-about-label">Build</span>
            <span className="sm-about-value">{import.meta.env.MODE}</span>
          </div>
        </div>
      </section>
    </div>
  )
}

// ── Root modal ────────────────────────────────────────────────────────────────

export function SettingsModal() {
  const isOpen = useSettingsStore((s) => s.settingsOpen)
  const activeSection = useSettingsStore((s) => s.activeSettingsSection)
  const setSection = useSettingsStore((s) => s.setActiveSettingsSection)
  const closeSettings = useSettingsStore((s) => s.closeSettings)

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeSettings()
      }
    },
    [isOpen, closeSettings],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) closeSettings()
  }

  return (
    <div
      className="settings-overlay"
      onClick={handleOverlayClick}
      data-testid="settings-modal-overlay"
    >
      <div
        className="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
        data-testid="settings-modal"
      >
        {/* Header */}
        <header className="settings-header">
          <span className="settings-title" id="settings-modal-title">Settings</span>
          <button
            className="settings-close-btn"
            onClick={closeSettings}
            title="Close settings"
            aria-label="Close settings"
            data-testid="settings-modal-close-button"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        {/* Sidebar + content */}
        <div className="settings-layout">
          <nav className="settings-nav" aria-label="Settings navigation">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                className={`settings-nav-item${activeSection === item.id ? ' active' : ''}`}
                aria-current={activeSection === item.id ? 'page' : undefined}
                onClick={() => setSection(item.id)}
                type="button"
                data-testid={`settings-modal-tab-${item.id}`}
              >
                <span className="settings-nav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>

          <div className="settings-content">
            {(() => {
              // Guard: if a persisted `activeSection` points at a section we've
              // since removed (data-display / notifications / developer),
              // fall back to 'general' so the modal isn't blank.
              const section = NAV_ITEMS.some((n) => n.id === activeSection)
                ? activeSection
                : 'general'
              switch (section) {
                case 'general': return <GeneralSection />
                case 'startup': return <StartupSection />
                case 'shortcuts': return <ShortcutsSection />
                case 'environment': return <EnvironmentSection />
                case 'updates': return (
                  // The existing <UpdatesSection> renders a self-bordered
                  // fieldset card, so wrap it in the same .settings-page
                  // shell the other sections use to inherit padding.
                  <div className="settings-page">
                    <UpdatesSection />
                  </div>
                )
                case 'about': return <AboutSection />
                default: return <GeneralSection />
              }
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}
